import { useEffect, useMemo } from 'react';
import { LocalStorageService } from '../services/local-storage-service';
import { useSelector, useDispatch } from 'react-redux';
import Global from '../store/global-selectors';
import { MarketplaceIds } from '../enums/marketplace-ids';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { useTrackMarketplaceConnection } from '../pages/settings/stores/tracking-events';
import { WorkflowSteps } from '../tracking/use-workflow-tracking';
import { refreshStoreList } from '../store/seller/seller.actions';

import { useScreenSizeContext } from '../contexts/screen-size-resolution';
import { useSegmentTracking } from '../pages/settings/stores/marketplace-connection-modal/onboard-page-notification-segment-tracking';
import { ONBOARDING_CONNECTION_SUCCESS_TOAST_CLICK, ONBOARDING_CONNECTION_SUCCESS_TOAST_CLOSE } from '../constants/analytics-events';
import { useAreFeatureFlagsReady } from '../feature-flags/hooks/use-are-feature-flags-ready';
import {
  MobileContent,
  DesktopContent,
  NotificationTitle,
  CONNECTED_MARKETPLACES_GUIDS,
} from '../pages/settings/stores/marketplace-connection-modal/store-connection-success-notifications';

const SHOPIFY_MARKETPLACE_ID = 14;

export const useConnectedMarketplacesChecker = () => {
  const dispatch = useDispatch();
  const allSellerStores = useSelector(Global.selectSellerStores);
  const activeSellerStores = useMemo(() => {
    return allSellerStores.filter((store) => store.isActive && store.marketplaceId !== MarketplaceIds.ShipStation);
  }, [allSellerStores]);
  const activeSellerStoreGuidList = useMemo(() => {
    return activeSellerStores.map((store) => store.storeGuid);
  }, [activeSellerStores]);
  const { isMobile } = useScreenSizeContext();
  const areFeatureFlagsReady = useAreFeatureFlagsReady();
  const trackBodyClick = useSegmentTracking(ONBOARDING_CONNECTION_SUCCESS_TOAST_CLICK);
  const trackClose = useSegmentTracking(ONBOARDING_CONNECTION_SUCCESS_TOAST_CLOSE);
  const trackMarketplaceConnection = useTrackMarketplaceConnection();

  useEffect(() => {
    const marketplacesSnapShot = LocalStorageService.getItem(CONNECTED_MARKETPLACES_GUIDS);
    const newMarketplaceGuid = marketplacesSnapShot
      ? activeSellerStoreGuidList.find((marketplace) => !marketplacesSnapShot.includes(marketplace))
      : null;
    const newMarketplace = allSellerStores.find((store) => store.storeGuid === newMarketplaceGuid);
    if (newMarketplaceGuid && areFeatureFlagsReady && newMarketplace?.marketplaceId !== SHOPIFY_MARKETPLACE_ID) {
      const saveAndNotify = () => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: NotificationTitle,
          content: isMobile ? MobileContent : DesktopContent,
          dontHide: true,
          onClick: trackBodyClick,
          trackNotificationClose: trackClose,
          withMobileStyling: isMobile,
        });
      };

      dispatch(refreshStoreList());
      saveAndNotify();
    }
    if (newMarketplaceGuid) {
      trackMarketplaceConnection(WorkflowSteps.COMPLETE, {
        selectedMarketplace: newMarketplace,
        marketplaceName: newMarketplace?.name,
        marketplaceId: newMarketplace?.marketplaceId,
      });
      LocalStorageService.clearItemsWithPrefix(CONNECTED_MARKETPLACES_GUIDS);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [areFeatureFlagsReady]);
};
